/**
 * Live patrol state + connectivity for one robot, from the PATROL_STATE_UPDATE
 * socket event. Liveness is inferred from ANY traffic for this serialID, since a
 * disconnect event can be missed; `frontend:get-device` is asked for explicitly
 * because joining a room does not itself return current connection state.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { getSocket, subscribeToRobot, unsubscribeFromRobot } from "../services/socketService";

// States patrol_runner.py can report once a patrol loop is no longer actively
// running — anything else ("running", "paused") counts as still active.
const PATROL_INACTIVE_STATES = new Set(["stopped", "completed", "idle", "error"]);

// Of those, the ones that mean a run actually ENDED. `idle` is excluded because
// it is also the resting state of a robot that has never patrolled, so treating
// it as an ending would pop a completion screen on first connect.
const PATROL_TERMINAL_STATES = new Set(["completed", "stopped", "error"]);

export function usePatrolStatus(serialID) {
  const [patrolState, setPatrolState] = useState(null);
  const [isPatrolling, setIsPatrolling] = useState(false);
  const [isRobotConnected, setIsRobotConnected] = useState(false);
  // Percentage (0-100), or null until a reading arrives. Derived from the same
  // BATTERY_STATUS_UPDATE/SENSOR_STATUS_UPDATE events already subscribed to
  // below for connectivity — no second socket, matching how
  // RoomNumberPageAero.jsx derives its own batteryLevel.
  const [batteryLevel, setBatteryLevel] = useState(null);
  // How the run that just finished ended, or null. `isPatrolling: false` alone
  // cannot distinguish finished / halted / failed / never-ran.
  const [terminalState, setTerminalState] = useState(null);
  // Only a run that was observed RUNNING can end. Without this, connecting to a
  // robot already sitting at "completed" from a previous session would show a
  // completion screen for a run this page never saw.
  const sawRunningRef = useRef(false);
  const serialRef = useRef(serialID);
  serialRef.current = serialID;

  const seedIsPatrolling = useCallback((value) => {
    setIsPatrolling(value);
    // Starting a patrol clears the previous run's outcome, and arms the
    // transition detector so the next terminal state is attributable to it.
    if (value) {
      setTerminalState(null);
      sawRunningRef.current = true;
    }
  }, []);

  // Called once the operator has acknowledged the finished run, so the page can
  // return to the setup form.
  const clearTerminalState = useCallback(() => {
    setTerminalState(null);
    sawRunningRef.current = false;
  }, []);

  useEffect(() => {
    // Reset on every robot change so the previous robot's connectivity — or the
    // outcome of ITS last run — can never be read as the newly selected one's.
    setIsRobotConnected(false);
    setTerminalState(null);
    sawRunningRef.current = false;
    setBatteryLevel(null);
    if (!serialID) {
      setPatrolState(null);
      return undefined;
    }

    const socket = getSocket();
    subscribeToRobot(serialID);
    socket.emit("frontend:get-device", { robotId: serialID });

    // An event with no serialID is not attributable to another robot, so it
    // is accepted; one naming a different robot is ignored.
    const matches = (data) =>
      !data?.serialID || String(data.serialID) === String(serialRef.current);

    const onPatrolState = (data) => {
      if (!matches(data)) return;
      setPatrolState(data);
      const state = String(data?.state || "").toLowerCase();
      if (state) {
        const active = !PATROL_INACTIVE_STATES.has(state);
        setIsPatrolling(active);
        if (active) {
          // A run is under way: remember it, so its ending is recognised.
          sawRunningRef.current = true;
          setTerminalState(null);
        } else if (sawRunningRef.current && PATROL_TERMINAL_STATES.has(state)) {
          // Latched rather than derived, so it survives the updates a robot
          // keeps emitting and stays up until acknowledged.
          setTerminalState(state);
          sawRunningRef.current = false;
        }
      }
      setIsRobotConnected(true);
    };
    const onDeviceData = (data) => {
      if (!matches(data)) return;
      setIsRobotConnected(!!data?.pythonSocketId);
    };
    const onConnected = (data) => {
      if (!matches(data)) return;
      setIsRobotConnected(true);
    };
    const onDisconnected = (data) => {
      if (!matches(data)) return;
      setIsRobotConnected(false);
    };
    // Any other traffic for this robot proves it is live even if the explicit
    // connect event was missed.
    const markConnected = (data) => {
      if (matches(data)) setIsRobotConnected(true);
    };

    // Percentage (0-100) from whichever of the two events last carried one —
    // same derivation as RoomNumberPageAero.jsx's socket handlers, applied
    // only when the event is attributable to this robot (same `matches` gate
    // every other handler above uses).
    const onBatteryStatus = (data) => {
      if (!matches(data)) return;
      const detail = data?.battery_detail ?? null;
      const raw = data?.batteryLevel;
      let level = null;
      if (typeof raw === "number") {
        level = raw;
      } else if (typeof detail?.percentage === "number") {
        level = Math.round(detail.percentage * 100);
      }
      if (typeof level === "number") setBatteryLevel(level);
      setIsRobotConnected(true);
    };
    const onSensorStatus = (data) => {
      if (!matches(data)) return;
      if (typeof data?.battery === "number") setBatteryLevel(Math.round(data.battery * 100));
      setIsRobotConnected(true);
    };

    socket.on("PATROL_STATE_UPDATE", onPatrolState);
    socket.on("get_device_data", onDeviceData);
    socket.on("robot_connected", onConnected);
    socket.on("robot_disconnected", onDisconnected);
    socket.on("nav_progress", markConnected);
    socket.on("BATTERY_STATUS_UPDATE", onBatteryStatus);
    socket.on("SENSOR_STATUS_UPDATE", onSensorStatus);

    return () => {
      socket.off("PATROL_STATE_UPDATE", onPatrolState);
      socket.off("get_device_data", onDeviceData);
      socket.off("robot_connected", onConnected);
      socket.off("robot_disconnected", onDisconnected);
      socket.off("nav_progress", markConnected);
      socket.off("BATTERY_STATUS_UPDATE", onBatteryStatus);
      socket.off("SENSOR_STATUS_UPDATE", onSensorStatus);
      unsubscribeFromRobot(serialID);
    };
  }, [serialID]);

  return {
    patrolState,
    isPatrolling,
    setIsPatrolling: seedIsPatrolling,
    isRobotConnected,
    batteryLevel,
    terminalState,
    clearTerminalState,
  };
}

export default usePatrolStatus;
